import { Panel, Box, Grid, GridItem } from '@sequencehq/core-components'
import SpinnerContainer from 'components/Loading/SpinnerContainer'
import { QuoteEditorContent } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/QuoteEditorContent'
import { QuoteEditorHeader } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/QuoteEditorHeader'
import { useQuoteEditorLayout } from 'modules/Cube/view/layouts/quote/useQuoteEditorLayout'
import { QuoteEditorInspector } from './QuoteEditorInspector/QuoteEditorInspector'
import { CoverImageEditor } from './QuoteEditorContent/CoverImageEditor/CoverImageEditor'
import { CoverImageEditorProvider } from './QuoteEditorContent/CoverImageEditor/CoverImageEditorContext'
import { QuoteTemplateEditorHeader } from './QuoteTemplateEditorHeader/QuoteTemplateEditorHeader'
import { NotAuthorizedScreen, usePermissions } from 'lib/rbac'
import { usePreparingSignatureRequestPolling } from './hooks'
import { AutoDisplayQuoteTemplateModal } from './AutoDisplayQuoteTemplateModal/AutoDisplayQuoteTemplateModal'
import { QuoteApprovalSimulationProvider } from './QuoteApprovalSimulationProvider'
import { SupersededQuoteBanner } from './SupersededQuoteBanner/SupersededQuoteBanner'
import { AmendmentOutForSignatureBanner } from './AmendmentOutForSignatureBanner/AmendmentOutForSignatureBanner'
import { QuoteSyncProvider } from './sync'
import { QuoteContentColumn } from './QuoteContentColumn/QuoteContentColumn'

/**
 * TODO: migrate this to use StandardLayout from core-components
 */
export const QuoteEditorLayout = () => {
  const { ready, isATemplate, quoteId } = useQuoteEditorLayout()
  const { can } = usePermissions()

  usePreparingSignatureRequestPolling({ ready })

  if (!can('org.quote:update')) {
    return <NotAuthorizedScreen />
  }

  if (!ready) {
    return <SpinnerContainer />
  }

  return (
    <QuoteApprovalSimulationProvider quoteId={quoteId} isEnabled={!isATemplate}>
      <QuoteSyncProvider>
        <Panel width="100%" height="100%">
          <Grid
            data-testid="quote.layout"
            templateAreas={`
    "header"
    "content"
    `}
            templateRows={`auto 1fr`}
            columnGap="20px"
            width="100%"
            height="100%"
            // The inner content grid has a 1218px min-width; scroll it within the
            // panel so the sidebar column stays reachable on narrow viewports.
            overflow="auto"
          >
            <GridItem
              data-testid="quote.layout.header"
              gridArea="header"
              position="relative"
              zIndex="2"
            >
              {isATemplate ? (
                <QuoteTemplateEditorHeader />
              ) : (
                <QuoteEditorHeader />
              )}
            </GridItem>
            <CoverImageEditorProvider>
              <Grid
                data-testid="quote.layout.content"
                position="relative"
                zIndex="1"
                templateColumns={`1fr auto`}
                templateRows={`1fr`}
                templateAreas={`
          "main sidebar"
          `}
                gridArea="content"
                height="100%"
                minWidth="1218px"
                overflow="hidden"
              >
                <Box
                  overflow="auto"
                  data-testid="quote.layout.content.main"
                  gridArea="main"
                >
                  {/* Above the editor itself rather than the whole page, so it sits over the
                      quote it is talking about and leaves the inspector alone. */}
                  <QuoteContentColumn>
                    <SupersededQuoteBanner />
                    <AmendmentOutForSignatureBanner />
                  </QuoteContentColumn>

                  <GridItem width="100%">
                    <CoverImageEditor />
                  </GridItem>

                  <GridItem
                    width="100%"
                    minHeight="calc(100% - 16px)"
                    overflow="auto"
                  >
                    <QuoteContentColumn>
                      <QuoteEditorContent />
                    </QuoteContentColumn>
                  </GridItem>
                </Box>

                {/* `minHeight="0"` lets the inspector scroll instead of stretching the row — see the Inspector docs. */}
                <GridItem
                  gridArea="sidebar"
                  zIndex="10"
                  height="100%"
                  minHeight="0"
                >
                  <QuoteEditorInspector />
                </GridItem>
              </Grid>
            </CoverImageEditorProvider>
          </Grid>
          <AutoDisplayQuoteTemplateModal />
        </Panel>
      </QuoteSyncProvider>
    </QuoteApprovalSimulationProvider>
  )
}
